import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { Observable, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import { SessionService } from "../session.service";
import { AccountPrerequisitesService } from "./account-prerequisites.service";
import {
  AccountPrerequisiteDialogComponent,
  AccountPrerequisiteDialogData,
} from "../shared/dialogs/account-prerequisite-dialog/account-prerequisite-dialog.component";
import { LocalStorageService } from "../storage/local-storage.service";

@Injectable({
  providedIn: "root",
})
export class AccountPrerequisitesGuard implements CanActivate {
  // Paths that should never be blocked by prerequisites
  private readonly bypassPaths = [
    /^auth\//,
    /^error$/,
    /^notFound$/,
  ];

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private sessionService: SessionService,
    private prerequisitesService: AccountPrerequisitesService,
    private localStorageService: LocalStorageService,
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> {
    const currentUser = this.sessionService.currentUserValue;
    const currentAccount = this.sessionService.currentAccountValue;

    if (!currentUser || !currentAccount) {
      // Let existing auth guards handle this.
      return of(true);
    }

    // Only run prerequisites when guided setup is enabled for this account.
    if (
      !this.localStorageService.isGuidedSetupEnabled(
        currentAccount.accountID,
      )
    ) {
      return of(true);
    }

    const url = state.url.startsWith("/") ? state.url.slice(1) : state.url;
    if (this.shouldBypass(url)) {
      return of(true);
    }

    return this.prerequisitesService
      .getFirstMissingForCurrentAccount()
      .pipe(
        switchMap((missing) => {
          if (!missing) {
            return of(true);
          }

          // If user is already navigating to the recommended route, do not block.
          if (this.matchesRecommendedRoute(url, missing.recommendedRoute)) {
            return of(true);
          }

          const dialogData: AccountPrerequisiteDialogData = {
            missingLabel: missing.label,
          };

          const dialogRef = this.dialog.open(AccountPrerequisiteDialogComponent, {
            width: "420px",
            data: dialogData,
          });

          return dialogRef.afterClosed().pipe(
            switchMap((result) => {
              if (result === "disable") {
                this.localStorageService.setGuidedSetupEnabled(
                  currentAccount.accountID,
                  false,
                );
                // Allow the original navigation to proceed with guided setup disabled.
                return of(true);
              }
              if (result === "go") {
                this.router.navigate(missing.recommendedRoute);
              }
              // Always cancel the original navigation; either the user stays put
              // or we programmatically navigate to the setup route.
              return of(false);
            }),
          );
        }),
      );
  }

  private shouldBypass(url: string): boolean {
    // Strip query params and fragments
    const path = url.split("?")[0].split("#")[0];

    return this.bypassPaths.some((pattern) => pattern.test(path));
  }

  private matchesRecommendedRoute(url: string, recommendedRoute: any[]): boolean {
    const path = url.split("?")[0].split("#")[0];
    const urlSegments = path.split("/").filter((seg) => !!seg);
    const routeSegments = recommendedRoute
      .map((segment) => (typeof segment === "string" ? segment : null))
      .filter((segment): segment is string => !!segment);

    if (routeSegments.length > urlSegments.length) {
      return false;
    }

    for (let i = 0; i < routeSegments.length; i++) {
      if (routeSegments[i] !== urlSegments[i]) {
        return false;
      }
    }

    return true;
  }
}


